const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.picklecourts.rent';
const TOKEN_KEY = 'picklecourts_data_token';
const USER_KEY = 'picklecourts_data_user';

export interface ExecutiveDashboardData {
  kpis: {
    totalUsers: number;
    newUsersInPeriod: number;
    totalBookings: number;
    bookingsInPeriod: number;
    confirmedBookingsInPeriod: number;
    revenueInPeriodCents: number;
    platformFeeInPeriodCents: number;
    promosRedeemed: number;
    promosConsumed: number;
  };
  bookingsByVenue: Array<{
    venueId: string;
    venueName: string;
    count: number;
    confirmedCount: number;
    revenueCents: number;
  }>;
  bookingsOverTime: Array<{ date: string; count: number }>;
  regionViews: Array<{ regionName: string; views: number; uniqueVisitors: number }>;
  venueViews: Array<{ venueId: string; venueName: string; views: number; uniqueVisitors: number }>;
  timeslotClicks: Array<{ venueId: string; venueName: string; clicks: number }>;
  externalBookingClicks: Array<{ venueId: string; venueName: string; clicks: number }>;
  viewerIntel: Array<{
    viewerKey: string;
    deviceFingerprint: string | null;
    ipAddress: string | null;
    userAgent: string | null;
    userId: string | null;
    userEmail: string | null;
    firstName: string | null;
    lastName: string | null;
    firstSeenAt: string;
    lastSeenAt: string;
    eventCount: number;
    regionViewCount: number;
    venueViewCount: number;
    timeslotClickCount: number;
    externalBookingClickCount: number;
    isBot: boolean;
    regions: string[];
    venues: string[];
    eventTypes: string[];
  }>;
  trafficQuality: {
    excludedEventCount: number;
    excludedViewerCount: number;
    internalEmails: string[];
    internalDomains: string[];
    internalIps: string[];
    internalIpPrefixes: string[];
    internalFingerprints: string[];
    internalFingerprintPrefixes: string[];
  };
  promoSummary: Array<{
    code: string;
    venueName: string | null;
    percentOff: number;
    redeemed: number;
    consumed: number;
  }>;
  recentBookings: Array<{
    id: string;
    confirmationCode: string;
    status: string;
    createdAt: string;
    startTime: string;
    endTime: string;
    paymentAmountCents: number | null;
    customerName: string;
    customerEmail: string | null;
    courtName: string;
    venueName: string;
  }>;
  recentUsers: Array<{
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    createdAt: string;
  }>;
}

export interface SuperadminExecutiveMetrics {
  timestamp: string;
  users: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    rows: Array<{
      id: string;
      email: string;
      firstName: string | null;
      lastName: string | null;
      phone: string | null;
      createdAt: string;
      bookingCount: number;
      lastBookingCreatedAt: string | null;
    }>;
  };
  bookings: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    rows: Array<{
      id: string;
      confirmationCode: string;
      status: string;
      createdAt: string;
      startTime: string;
      endTime: string;
      customerName: string;
      customerEmail: string | null;
      courtName: string;
      venueName: string;
    }>;
  };
}

export interface CloudflareAnalytics {
  configured: boolean;
  error?: string;
  days?: number;
  totalRequests?: number;
  totalPageViews?: number;
  landingPageHits?: number;
  appHits?: number;
  regionPageHits?: number;
  usHits?: number;
  wafBlocks?: number;
  scannerTotal?: number;
  topPages?: Array<{ path: string; count: number }>;
  topCountries?: Array<{ code: string; country: string; count: number }>;
  daily?: Array<{ date: string; pageViews: number; total: number }>;
}

class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  private getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  private async request<T>(path: string, options?: RequestInit): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(options?.headers as Record<string, string> || {}),
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const res = await fetch(`${this.baseUrl}${path}`, { ...options, headers });

    if (res.status === 401) {
      // Clear both token and user so AuthContext sees logged-out state
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      // Only redirect if we're not already on the login page to avoid loops
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
      throw new Error('Session expired');
    }

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || `Request failed (${res.status})`);
    }
    return data;
  }

  async login(email: string, password: string): Promise<{ token: string; admin: { id: string; email: string; role: string; venueId: string | null } }> {
    return this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email: email.trim(), password }),
    });
  }

  async getSystemHealth(): Promise<any> {
    return this.request('/api/admin/system-health');
  }

  async getExecutiveDashboard(period: string = '30d'): Promise<ExecutiveDashboardData> {
    return this.request(`/api/admin/superadmin/executive-dashboard?period=${encodeURIComponent(period)}`);
  }

  async getCloudflareAnalytics(days: number = 7): Promise<CloudflareAnalytics> {
    return this.request(`/api/admin/superadmin/executive-dashboard/cloudflare-analytics?days=${days}`);
  }

  async getSuperadminExecutiveMetrics(params?: {
    usersPage?: number;
    usersLimit?: number;
    bookingsPage?: number;
    bookingsLimit?: number;
  }): Promise<SuperadminExecutiveMetrics> {
    const queryParams = new URLSearchParams();
    if (params?.usersPage) queryParams.set('usersPage', String(params.usersPage));
    if (params?.usersLimit) queryParams.set('usersLimit', String(params.usersLimit));
    if (params?.bookingsPage) queryParams.set('bookingsPage', String(params.bookingsPage));
    if (params?.bookingsLimit) queryParams.set('bookingsLimit', String(params.bookingsLimit));
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.request(`/api/admin/superadmin/executive-metrics${query}`);
  }
}

export const api = new ApiClient();
